import type { FilterConditionOutput } from '@shared/components/ui/AdvancedFilter';
import { useCallback, useEffect, useState } from 'react';
import type { DateRange } from 'react-day-picker';

interface ExtraWorkOrdersFiltersState {
  status: string[];
  team_id: string;
  entry_id: string;
  user_id: string;
  location_id: string;
  started_at: DateRange | undefined;
  finished_at: DateRange | undefined;
  deadline_at: DateRange | undefined;
  created_at: DateRange | undefined;
}

interface UseExtraWorkOrdersFiltersProps {
  initialConditions?: FilterConditionOutput[];
  onChange?: (conditions: FilterConditionOutput[]) => void;
}

function formatDateForAPI(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export const useExtraWorkOrdersFilters = ({ initialConditions = [], onChange }: UseExtraWorkOrdersFiltersProps) => {
  const [filters, setFilters] = useState<ExtraWorkOrdersFiltersState>(() => parseConditionsToState(initialConditions));

  const convertStateToConditions = useCallback((state: ExtraWorkOrdersFiltersState): FilterConditionOutput[] => {
    const conditions: FilterConditionOutput[] = [];

    if (state.status.length > 0) {
      conditions.push({
        column: 'status',
        action: 'in',
        value: state.status,
      });
    }

    if (state.team_id.trim()) {
      conditions.push({
        column: 'team_id',
        action: 'in',
        value: [state.team_id.trim()],
      });
    }
    if (state.entry_id.trim()) {
      conditions.push({
        column: 'entry_id',
        action: 'in',
        value: [state.entry_id.trim()],
      });
    }
    if (state.user_id.trim()) {
      conditions.push({
        column: 'user_id',
        action: 'in',
        value: [state.user_id.trim()],
      });
    }
    if (state.location_id.trim()) {
      conditions.push({
        column: 'location_id',
        action: 'in',
        value: [state.location_id.trim()],
      });
    }

    if (state.started_at?.from && state.started_at?.to) {
      conditions.push({
        column: 'started_at',
        action: 'greater_equal',
        value: formatDateForAPI(state.started_at.from),
      });
      conditions.push({
        column: 'started_at',
        action: 'less_equal',
        value: formatDateForAPI(state.started_at.to),
      });
    }
    if (state.finished_at?.from && state.finished_at?.to) {
      conditions.push({
        column: 'finished_at',
        action: 'greater_equal',
        value: formatDateForAPI(state.finished_at.from),
      });
      conditions.push({
        column: 'finished_at',
        action: 'less_equal',
        value: formatDateForAPI(state.finished_at.to),
      });
    }
    if (state.deadline_at?.from && state.deadline_at?.to) {
      conditions.push({
        column: 'deadline_at',
        action: 'greater_equal',
        value: formatDateForAPI(state.deadline_at.from),
      });
      conditions.push({
        column: 'deadline_at',
        action: 'less_equal',
        value: formatDateForAPI(state.deadline_at.to),
      });
    }
    if (state.created_at?.from && state.created_at?.to) {
      conditions.push({
        column: 'created_at',
        action: 'greater_equal',
        value: formatDateForAPI(state.created_at.from),
      });
      conditions.push({
        column: 'created_at',
        action: 'less_equal',
        value: formatDateForAPI(state.created_at.to),
      });
    }

    return conditions;
  }, []);

  const updateFilters = useCallback(
    (updates: Partial<ExtraWorkOrdersFiltersState>) => {
      setFilters((prev) => {
        const newState = { ...prev, ...updates };
        onChange?.(convertStateToConditions(newState));
        return newState;
      });
    },
    [convertStateToConditions, onChange],
  );

  const handleStatusChange = useCallback((values: string[]) => updateFilters({ status: values }), [updateFilters]);
  const handleTeamIdChange = useCallback((value: string) => updateFilters({ team_id: value }), [updateFilters]);
  const handleEntryIdChange = useCallback((value: string) => updateFilters({ entry_id: value }), [updateFilters]);
  const handleUserIdChange = useCallback((value: string) => updateFilters({ user_id: value }), [updateFilters]);
  const handleLocationIdChange = useCallback((value: string) => updateFilters({ location_id: value }), [updateFilters]);
  const handleStartedAtChange = useCallback(
    (range: DateRange | undefined) => updateFilters({ started_at: range }),
    [updateFilters],
  );
  const handleFinishedAtChange = useCallback(
    (range: DateRange | undefined) => updateFilters({ finished_at: range }),
    [updateFilters],
  );
  const handleDeadlineAtChange = useCallback(
    (range: DateRange | undefined) => updateFilters({ deadline_at: range }),
    [updateFilters],
  );
  const handleCreatedAtChange = useCallback(
    (range: DateRange | undefined) => updateFilters({ created_at: range }),
    [updateFilters],
  );

  const clearAllFilters = useCallback(() => {
    const emptyState: ExtraWorkOrdersFiltersState = {
      status: [],
      team_id: '',
      entry_id: '',
      user_id: '',
      location_id: '',
      started_at: undefined,
      finished_at: undefined,
      deadline_at: undefined,
      created_at: undefined,
    };
    setFilters(emptyState);
    onChange?.([]);
  }, [onChange]);

  useEffect(() => {
    setFilters(parseConditionsToState(initialConditions));
  }, [initialConditions]);

  return {
    filters,
    handleStatusChange,
    handleTeamIdChange,
    handleEntryIdChange,
    handleUserIdChange,
    handleLocationIdChange,
    handleStartedAtChange,
    handleFinishedAtChange,
    handleDeadlineAtChange,
    handleCreatedAtChange,
    clearAllFilters,
  };
};

function parseConditionsToState(conditions: FilterConditionOutput[]): ExtraWorkOrdersFiltersState {
  const state: ExtraWorkOrdersFiltersState = {
    status: [],
    team_id: '',
    entry_id: '',
    user_id: '',
    location_id: '',
    started_at: undefined,
    finished_at: undefined,
    deadline_at: undefined,
    created_at: undefined,
  };

  const byColumn: Record<string, FilterConditionOutput[]> = {};
  conditions.forEach((c) => {
    if (!byColumn[c.column]) byColumn[c.column] = [];
    byColumn[c.column].push(c);
  });

  const getArray = (col: string) => {
    const c = byColumn[col]?.find((x) => x.action === 'in');
    if (!c?.value) return [];
    return Array.isArray(c.value) ? c.value : [String(c.value)];
  };
  const getFirst = (col: string) => {
    const arr = getArray(col);
    return arr[0] ?? '';
  };

  state.status = getArray('status');
  state.team_id = getFirst('team_id');
  state.entry_id = getFirst('entry_id');
  state.user_id = getFirst('user_id');
  state.location_id = getFirst('location_id');

  const parseDateRange = (col: string) => {
    const from = byColumn[col]?.find((c) => c.action === 'greater_equal');
    const to = byColumn[col]?.find((c) => c.action === 'less_equal');
    if (from?.value && to?.value) {
      return {
        from: new Date(String(from.value)),
        to: new Date(String(to.value)),
      };
    }
    return undefined;
  };

  state.started_at = parseDateRange('started_at');
  state.finished_at = parseDateRange('finished_at');
  state.deadline_at = parseDateRange('deadline_at');
  state.created_at = parseDateRange('created_at');

  return state;
}
